import React, { createContext, useContext, useState, useRef, useCallback, ReactNode } from 'react';
import { useQuery } from '@apollo/client';
import { MY_PROFILE_QUERY } from '~/graphql/queries';

type TabType = 'account' | 'assistant' | 'notifications';

interface ProfileContextType {
  // Data
  data: any;
  loading: boolean;
  error: any;

  // Form state
  activeTab: TabType;
  hasChanges: boolean;
  hasErrors: boolean;
  isSaving: boolean;
  setIsSaving: (isSaving: boolean) => void;

  // Form refs
  formRefs: React.MutableRefObject<{ [key in TabType]?: HTMLFormElement | null }>;
  resetFunctions: React.MutableRefObject<
    { [key in TabType]?: (() => void) | null } & { [key in `${TabType}_save`]?: (() => void | Promise<void>) | null }
  >;

  // Actions
  setActiveTab: (tab: TabType) => void;
  handleFormChange: (tabId: TabType, isDirty: boolean, hasErrors: boolean) => void;
  handleSave: () => Promise<void>;
  handleFormSave: (values?: any) => Promise<void>;
  handleDiscardChanges: () => void;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

interface ProfileProviderProps {
  children: ReactNode;
}

export const ProfileProvider: React.FC<ProfileProviderProps> = ({ children }) => {
  const [activeTab, setActiveTab] = useState<TabType>('assistant');
  const [hasChanges, setHasChanges] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const formRefs = useRef<{ [key in TabType]?: HTMLFormElement | null }>({});
  const resetFunctions = useRef<
    { [key in TabType]?: (() => void) | null } & { [key in `${TabType}_save`]?: (() => void | Promise<void>) | null }
  >({});

  const { data, loading, error } = useQuery(MY_PROFILE_QUERY, {
    fetchPolicy: 'network-only',
  });

  const handleFormChange = useCallback(
    (tabId: TabType, isDirty: boolean, hasErrors: boolean) => {
      if (tabId === activeTab) {
        setHasChanges(isDirty);
        setHasErrors(hasErrors);
      }
    },
    [activeTab]
  );

  const handleSave = useCallback(async () => {
    const currentFormRef = formRefs.current[activeTab];
    if (currentFormRef) {
      setIsSaving(true);
      try {
        // Trigger form submission
        currentFormRef.requestSubmit();
      } catch (error) {
        console.error('Form submission error:', error);
        setIsSaving(false);
      }
    } else {
      // For tabs without a form ref, check for a save function
      const saveFn = resetFunctions.current[`${activeTab}_save`];
      if (saveFn) {
        setIsSaving(true);
        try {
          await saveFn();
        } catch (error) {
          console.error('Save error:', error);
        } finally {
          setIsSaving(false);
        }
      }
    }
  }, [activeTab, resetFunctions]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleFormSave = useCallback(async (_values?: any) => {
    setIsSaving(false);
    setHasChanges(false);
    setHasErrors(false);
  }, []);

  const setActiveTabMemo = useCallback((newTab: TabType) => {
    setActiveTab(newTab);
  }, []);

  const handleDiscardChanges = useCallback(() => {
    // Call the reset function directly
    const resetFn = resetFunctions.current[activeTab];

    if (resetFn) {
      resetFn();
    }
    // Reset state after a brief delay to allow form reset to complete
    setTimeout(() => {
      setHasChanges(false);
      setHasErrors(false);
    }, 100);
  }, [activeTab]);

  const value: ProfileContextType = {
    data,
    loading,
    error,
    activeTab,
    hasChanges,
    hasErrors,
    isSaving,
    setIsSaving,
    formRefs,
    resetFunctions,
    setActiveTab: setActiveTabMemo,
    handleFormChange,
    handleSave,
    handleFormSave,
    handleDiscardChanges,
  };

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};

export const useProfile = (): ProfileContextType => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};
